import { 
  orders, 
  departments, 
  stages, 
  workItems, 
  stageColors, 
  priorityColors, 
  statusColors 
} from '@/data/mockData';
import { 
  Order, 
  Department, 
  Stage, 
  WorkItem, 
  ProductionSchedule, 
  DepartmentSummary 
} from '@/data/types';

// 訂單相關查詢
export const getAllOrders = (): Order[] => {
  return orders;
};

export const getOrdersByStage = (stageId?: string): Order[] => {
  if (!stageId) return orders;
  return orders.filter(order => order.stage === stageId);
};

export const getOrderById = (orderId: string): Order | undefined => {
  return orders.find(order => order.id === orderId);
};

export const getOrdersInProduction = (): Order[] => {
  return orders.filter(order => order.stage === 'production');
};

// 部門相關查詢
export const getAllDepartments = (): Department[] => {
  return departments;
};

export const getDepartmentsByType = (type: 'administrative' | 'production'): Department[] => {
  return departments.filter(dept => dept.type === type);
};

export const getDepartmentById = (deptId: string): Department | undefined => {
  return departments.find(dept => dept.id === deptId);
};

// 階段相關查詢
export const getAllStages = (): Stage[] => {
  return stages.sort((a, b) => a.order - b.order);
};

export const getStageById = (stageId: string): Stage | undefined => {
  return stages.find(stage => stage.id === stageId);
};

export const getResponsibleDepartments = (stageId: string): Department[] => {
  const stage = getStageById(stageId);
  if (!stage) return [];
  
  return departments.filter(dept => 
    stage.responsibleDepartments.includes(dept.id)
  );
};

// 工作項目相關查詢
export const getWorkItemsByDepartment = (departmentName: string, date?: Date): WorkItem[] => {
  let filtered = workItems.filter(item => item.department === departmentName);
  
  if (date) {
    const dateStr = date.toISOString().split('T')[0];
    filtered = filtered.filter(item => item.date === dateStr);
  }
  
  return filtered;
};

export const getWorkItemsByOrder = (orderId: string): WorkItem[] => {
  return workItems.filter(item => item.orderId === orderId);
};

export const getWorkItemsByStage = (stage: string): WorkItem[] => {
  return workItems.filter(item => item.stage === stage);
};

export const getWorkItemsForDate = (date: Date): WorkItem[] => {
  const dateStr = date.toISOString().split('T')[0];
  return workItems.filter(item => item.date === dateStr);
};

// 生產排程相關
export const getProductionScheduleByOrderId = (orderId: string): ProductionSchedule | null => {
  const order = getOrderById(orderId);
  if (!order || !order.departments) return null;
  
  const overallProgress = calculateOverallProgress(order.departments);
  const currentDepartment = getCurrentDepartment(order.departments);
  
  return {
    orderId,
    departments: order.departments,
    overallProgress,
    currentDepartment
  };
};

export const calculateOverallProgress = (departments: any[]): number => {
  if (!departments || departments.length === 0) return 0;
  const totalProgress = departments.reduce((sum, dept) => sum + dept.progress, 0);
  return Math.round(totalProgress / departments.length);
};

export const getCurrentDepartment = (departments: any[]): string | null => {
  const inProgress = departments.find(dept => dept.progress > 0 && dept.progress < 100);
  return inProgress ? inProgress.name : null;
};

// 部門摘要統計
export const getDepartmentSummary = (departmentName: string, date: Date): DepartmentSummary => {
  const workItemsForDate = getWorkItemsByDepartment(departmentName, date);
  const totalHours = workItemsForDate.reduce((sum, item) => sum + item.estimatedHours, 0);
  
  // 模擬可用員工數據
  const availableWorkers = getAvailableWorkers(departmentName);
  
  return {
    department: departmentName,
    workItemsCount: workItemsForDate.length,
    availableWorkers,
    estimatedHours: totalHours
  };
};

// 輔助函數 - 取得部門可用員工數
const getAvailableWorkers = (departmentName: string): number => {
  const workerCounts: Record<string, number> = {
    '採購部': 3,
    '包膠部': 12,
    '倉庫部': 8,
    '車床部': 15,
    '品管部': 6,
    '包裝部': 8
  };
  
  return workerCounts[departmentName] || 5;
};

// 顏色相關工具
export const getStageColor = (stageId: string): string => {
  return stageColors[stageId as keyof typeof stageColors] || 'bg-gray-500';
};

export const getPriorityColor = (priority: string): string => {
  return priorityColors[priority as keyof typeof priorityColors] || 'bg-gray-500';
};

export const getStatusColor = (status: string): string => {
  return statusColors[status as keyof typeof statusColors] || 'bg-gray-500';
};

// 統計資料
export const getOrderStatistics = () => {
  const totalOrders = orders.length;
  const stageStats = stages.map(stage => ({
    stage: stage.name,
    count: getOrdersByStage(stage.id).length
  }));
  
  const priorityStats = ['high', 'medium', 'low'].map(priority => ({
    priority,
    count: orders.filter(order => order.priority === priority).length
  }));
  
  return {
    totalOrders,
    stageStats,
    priorityStats
  };
};

// 搜尋和過濾功能
export const searchOrders = (searchTerm: string, priorityFilter?: string): Order[] => {
  let filtered = orders;
  
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(order => 
      order.id.toLowerCase().includes(term) ||
      order.customer.toLowerCase().includes(term) ||
      order.product.toLowerCase().includes(term)
    );
  }
  
  if (priorityFilter && priorityFilter !== 'all') {
    filtered = filtered.filter(order => order.priority === priorityFilter);
  }
  
  return filtered;
};